import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  filterParametersSelector,
  reportFilterCountryCodeSelector,
  reportFilterDiscountsJoinSelector,
  reportGcsFilterSelector,
  reportGcsByTaxSelector,
  downloadReportPdf,
} from '../../state';
import { GcsByTax } from '../../types';
import handlerFactory, { GcsByTaxDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<GcsByTax, GcsByTaxDataGridContext>();

  const { t } = useTranslation();
  const title = t('gcs-taxes.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const gcsByTaxResource = useSelector(reportGcsByTaxSelector);
  const discountsJoin = useSelector(reportFilterDiscountsJoinSelector);
  const appParameters = useSelector(filterParametersSelector);
  const reportGcsFilters = useSelector(reportGcsFilterSelector);
  const storeExtraInfo = useMemo(
    () => appParameters.data?.storeExtraInfo ?? [],
    [appParameters.data?.storeExtraInfo]
  );

  const { data, loading } = gcsByTaxResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
      storeExtraInfo,
      discountsJoin,
    }),
    [t, country, storeExtraInfo, discountsJoin]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportGcsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<GcsByTax, GcsByTaxDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      XLSX: { includeTotals: false },
      PDF: {
        includeTotals: false,
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const GcsByTaxDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory} hideSummaryRow>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default GcsByTaxDataGrid;
